import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, FileText, AlertTriangle, Scale, Lock, Users, Database, Globe, Mail } from 'lucide-react';

export default function TermsConditions() {
  const lastUpdated = "April 4, 2026";

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0A0A1B 0%, #0D0D1F 100%)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(10, 10, 27, 0.8)', borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">Back to Privluxe</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: '#7C3AED' }} />
            <span className="text-sm" style={{ color: 'rgba(229,231,235,0.6)' }}>Legal</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.2) 0%, rgba(34,211,238,0.2) 100%)', border: '1px solid rgba(124,58,237,0.3)' }}>
            <FileText className="h-8 w-8" style={{ color: '#7C3AED' }} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Terms & Conditions</h1>
          <p style={{ color: 'rgba(229,231,235,0.6)' }}>Last updated: {lastUpdated}</p>
        </div>

        {/* Trust Message */}
        <div className="p-6 rounded-2xl mb-12" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(34,211,238,0.1) 100%)', border: '1px solid rgba(124,58,237,0.2)' }}>
          <p className="text-lg text-center" style={{ color: '#E5E7EB' }}>
            <span style={{ color: '#22D3EE' }}>Your conversations are private.</span><br />
            Privluxe is designed so only you and the recipient can access your messages.<br />
            <span style={{ color: 'rgba(229,231,235,0.5)' }}>No app. No traces. Full control.</span>
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-12">
          {/* Section 1 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <Scale className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">1. Acceptance of Terms</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>By accessing or using Privluxe ("Platform", "Service", "we", "us", or "our"), you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you must discontinue use immediately.</p>
              <p>These Terms constitute a legally binding agreement between you ("User", "you", or "your") and Privluxe. We reserve the right to modify these Terms at any time, and such modifications shall be effective immediately upon posting. Your continued use of the Platform after any modification indicates your acceptance of the modified Terms.</p>
              <p>You represent and warrant that you are at least 18 years of age and have the legal capacity to enter into these Terms. If you are accessing the Platform on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.</p>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <Globe className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">2. Nature of Service</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Privluxe is a private communication platform that enables users to exchange messages, media, voice communications, video calls, and other content through secure channels.</p>
              <p><strong className="text-white">Service Limitations:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We do not guarantee delivery, storage, or permanent availability of any communication</li>
                <li>Messages may be subject to automatic deletion based on user settings or platform policies</li>
                <li>Service availability may be interrupted for maintenance, updates, or unforeseen circumstances</li>
                <li>Features and functionality may change without prior notice</li>
                <li>We reserve the right to discontinue any aspect of the Service at any time</li>
              </ul>
              <p><strong className="text-white">Platform Access:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access is provided via web browser only - no application installation required</li>
                <li>Users are responsible for maintaining compatible devices and internet connectivity</li>
                <li>We do not guarantee compatibility with all browsers or devices</li>
                <li>Certain features may require specific browser capabilities (camera, microphone access)</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                <Users className="h-5 w-5" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">3. User Responsibility</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>You agree to use the Platform responsibly and in compliance with all applicable laws. Specifically, you agree:</p>
              <p><strong className="text-white">Prohibited Activities:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Not to use the Platform for any illegal activities, including but not limited to fraud, money laundering, or trafficking</li>
                <li>Not to harass, threaten, stalk, intimidate, or harm any other user or third party</li>
                <li>Not to upload, share, or transmit any content that is harmful, abusive, defamatory, obscene, or unlawful</li>
                <li>Not to impersonate any person or entity, or falsely represent your affiliation with any person or entity</li>
                <li>Not to attempt to gain unauthorized access to any portion of the Platform or any other systems or networks</li>
                <li>Not to use automated systems, bots, or scripts to access or interact with the Platform</li>
                <li>Not to interfere with or disrupt the Platform's operation or servers</li>
                <li>Not to transmit viruses, malware, or any other malicious code</li>
                <li>Not to collect or harvest user information without consent</li>
                <li>Not to use the Platform for commercial purposes without authorization</li>
              </ul>
              <p><strong className="text-white">Content Responsibility:</strong></p>
              <p>You are solely responsible for all content you create, upload, share, or transmit through the Platform. You retain ownership of your content but grant us a limited license to process and transmit it as necessary to provide the Service.</p>
              <p>You represent and warrant that you have all necessary rights and permissions to share any content you upload, and that such content does not infringe upon the intellectual property rights of any third party.</p>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <Lock className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">4. Privacy & Monitoring Disclaimer</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>While Privluxe is designed with privacy as a core principle, you acknowledge the following:</p>
              <p><strong className="text-white">Data Processing:</strong></p>
              <p>Certain system-level data may be processed for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Security and fraud prevention</li>
                <li>Platform stability and performance optimization</li>
                <li>Legal and regulatory compliance</li>
                <li>Abuse prevention and enforcement of our Terms</li>
                <li>Service improvement and analytics</li>
              </ul>
              <p><strong className="text-white">Monitoring Rights:</strong></p>
              <p>We reserve the right to monitor, review, or investigate activity on the Platform where required:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>By applicable law, regulation, or legal process</li>
                <li>For abuse prevention and user safety</li>
                <li>To protect the integrity of the Platform</li>
                <li>To enforce these Terms and our policies</li>
                <li>In response to user reports of violations</li>
              </ul>
              <p><strong className="text-white">Law Enforcement:</strong></p>
              <p>We may disclose information to law enforcement agencies, government officials, or other third parties when we believe in good faith that such disclosure is necessary to comply with applicable law, protect our rights, or prevent harm to individuals.</p>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <AlertTriangle className="h-5 w-5" style={{ color: '#EF4444' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">5. No Guarantee of Absolute Privacy</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>You expressly acknowledge and agree that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>No digital communication system is 100% secure</li>
                <li>You use the Platform at your own risk</li>
                <li>Privluxe is not liable for any unauthorized access, interception, or disclosure of communications that occurs beyond our reasonable control</li>
                <li>Third-party services, networks, or devices you use to access the Platform may have their own security vulnerabilities</li>
                <li>Other users with whom you communicate may share, screenshot, or otherwise disclose your communications</li>
                <li>Technical failures, software bugs, or human error may result in unintended data exposure</li>
              </ul>
              <p><strong className="text-white">Security Measures:</strong></p>
              <p>While we implement industry-standard security measures including encryption, access controls, and secure infrastructure, we cannot guarantee that these measures will be effective against all potential threats. You are responsible for protecting your own account credentials and devices.</p>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <Users className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">6. Account & Access</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p><strong className="text-white">Account Management:</strong></p>
              <p>We reserve the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Suspend or terminate accounts without prior notice for violations of these Terms</li>
                <li>Restrict access to certain features or functionality</li>
                <li>Remove or disable content that violates our policies</li>
                <li>Implement rate limits or usage restrictions</li>
                <li>Require additional verification for account access</li>
                <li>Modify, suspend, or discontinue any aspect of the Service</li>
              </ul>
              <p><strong className="text-white">Account Security:</strong></p>
              <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account or any other breach of security.</p>
              <p><strong className="text-white">Account Termination:</strong></p>
              <p>You may terminate your account at any time by contacting us. Upon termination, your right to use the Service will immediately cease. We may retain certain data as required by law or for legitimate business purposes.</p>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <Shield className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">7. Limitation of Liability</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</p>
              <p><strong className="text-white">Disclaimer of Warranties:</strong></p>
              <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.</p>
              <p><strong className="text-white">Limitation of Damages:</strong></p>
              <p>Privluxe shall not be liable for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Any loss of data, messages, or communications</li>
                <li>Indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, or business opportunities</li>
                <li>User misuse or third-party actions</li>
                <li>Service interruptions or downtime</li>
                <li>Actions of other users or third parties</li>
                <li>Any damages arising from your use of or inability to use the Service</li>
              </ul>
              <p>IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID US (IF ANY) IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                <Database className="h-5 w-5" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">8. Indemnification</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>You agree to indemnify, defend, and hold harmless Privluxe and its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses (including attorney's fees) arising from:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights, including intellectual property rights</li>
                <li>Your content or communications</li>
                <li>Any dispute between you and another user</li>
              </ul>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <FileText className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">9. Intellectual Property</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>The Platform and its original content, features, and functionality are owned by Privluxe and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.</p>
              <p>You may not copy, modify, distribute, sell, lease, or create derivative works based on the Platform without our prior written consent. The Privluxe name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of Privluxe.</p>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <Globe className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">10. Governing Law & Dispute Resolution</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.</p>
              <p>Any dispute arising out of or relating to these Terms or the Service shall be resolved through binding arbitration in accordance with the Arbitration and Conciliation Act, 1996. The arbitration shall be conducted in English and shall take place in Mumbai, India.</p>
              <p>Notwithstanding the above, we may seek injunctive or other equitable relief in any court of competent jurisdiction to protect our intellectual property rights.</p>
            </div>
          </section>

          {/* Section 11 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <Mail className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">11. Contact Information</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>If you have any questions about these Terms, please contact us at:</p>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <p><strong className="text-white">Privluxe Legal Team</strong></p>
                <p>Email: legal@privluxe.com</p>
                <p>Website: www.privluxe.com</p>
              </div>
            </div>
          </section>

          {/* Section 12 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <AlertTriangle className="h-5 w-5" style={{ color: '#EF4444' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">12. Severability & Entire Agreement</h2>
            </div>
            <div className="pl-13 space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>If any provision of these Terms is held to be invalid or unenforceable, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>
              <p>These Terms, together with our Privacy Policy and any other agreements expressly incorporated by reference, constitute the entire agreement between you and Privluxe concerning the Service.</p>
              <p>Our failure to enforce any right or provision of these Terms shall not be considered a waiver of those rights.</p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/privacy" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#A78BFA' }} />
              <span className="text-sm text-white">Privacy Policy</span>
            </Link>
            <Link to="/acceptable-use" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
              <Users className="h-6 w-6 mx-auto mb-2" style={{ color: '#22D3EE' }} />
              <span className="text-sm text-white">Acceptable Use</span>
            </Link>
            <Link to="/cookies" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <Database className="h-6 w-6 mx-auto mb-2" style={{ color: '#22C55E' }} />
              <span className="text-sm text-white">Cookie Policy</span>
            </Link>
            <Link to="/law-enforcement" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
              <Scale className="h-6 w-6 mx-auto mb-2" style={{ color: '#F59E0B' }} />
              <span className="text-sm text-white">Law Enforcement</span>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p style={{ color: 'rgba(229,231,235,0.4)' }}>© 2026 Privluxe. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
